import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { getFeatureFlagUUID } from '@dafiti/feature-flag-sdk'
import type { LogsInitConfiguration, User } from '@datadog/browser-logs'
import type { RumInitConfiguration } from '@datadog/browser-rum'
import type { ScriptDescriptor } from '@/types/script.type'
import type {
  InstrumentationProvider,
  InstrumentationSpan,
  InstrumentationUser,
  ObservabilityContext,
} from '@/next'
import { setUserFromAvailableIdentifiers } from '@/next'

export type DatadogBrowserInitOptions = LogsInitConfiguration &
  RumInitConfiguration

class DatadogBrowserSpan implements InstrumentationSpan {
  private readonly start = Date.now()

  constructor(
    private readonly name: string,
    private readonly baseCtx?: ObservabilityContext,
  ) {}

  addEvent(name: string, ctx?: ObservabilityContext): void {
    datadogRum.addAction(name, { ...this.baseCtx, ...ctx })
  }

  end(ctx?: ObservabilityContext): void {
    datadogRum.addAction(`[span] ${this.name}`, {
      duration: Date.now() - this.start,
      ...this.baseCtx,
      ...ctx,
    })
  }
}

export class DatadogBrowserAdapter implements InstrumentationProvider {
  constructor(private readonly opts: DatadogBrowserInitOptions) {
    this.init(opts as unknown as Record<string, unknown>)
  }

  init(options?: Record<string, unknown>): void {
    // Initialize Datadog RUM
    datadogRum.init({
      applicationId: this.opts.applicationId,
      clientToken: this.opts.clientToken,
      site: this.opts.site || 'datadoghq.com',
      service: this.opts.service,
      env: this.opts.env,
      version: this.opts.version,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 5,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      trackBfcacheViews: true,
      defaultPrivacyLevel: 'mask-user-input',
      ...options,
    })

    // Initialize Datadog Logs
    datadogLogs.init({
      sessionSampleRate: 100,
      forwardErrorsToLogs: true,
      site: this.opts.site || 'datadoghq.com',
      env: this.opts.env,
      clientToken: this.opts.clientToken,
      ...options,
    })

    datadogRum.startSessionReplayRecording()

    datadogRum.onReady(() => {
      setUserFromAvailableIdentifiers(this)
    })
  }

  getScripts(): Array<ScriptDescriptor> {
    return []
  }

  log(
    level: 'info' | 'warn' | 'error' | 'debug',
    message: string,
    context?: ObservabilityContext,
  ): void {
    datadogLogs.logger[level](message, context)
  }

  captureError(error: Error, context?: ObservabilityContext): void {
    datadogRum.addError(error, context)

    datadogLogs.logger.error(error.message, {
      error: error.stack,
      ...context,
    })
  }

  startSpan(name: string, context?: ObservabilityContext): InstrumentationSpan {
    return new DatadogBrowserSpan(name, context)
  }

  setUser(id: string, info?: InstrumentationUser): void {
    this.setMeta('feature_flag_uuid', getFeatureFlagUUID())

    datadogRum.setUserProperty('id', getFeatureFlagUUID())
    datadogLogs.setUserProperty('id', getFeatureFlagUUID())

    if (info?.customerId) {
      const user = {
        ...(info as unknown as User),
        id,
        customerId: info.customerId,
        email: info.email || '',
        name: info.userName || '',
      }

      datadogRum.setUser(user)
      datadogLogs.setUser(user)
    }
  }

  setMeta(key: string, value: unknown): void {
    datadogRum.setGlobalContextProperty(key, value)
    datadogLogs.setGlobalContextProperty(key, value)
  }

  event(name: string, data?: Record<string, unknown>): void {
    datadogRum.addAction(name, data)
  }
}
